import React, { useState, useEffect } from "react";
import LoadingSpinner from "../common/LoadingSpinner";
import Icon from "../common/Icon";
import { studiesAPI } from "../../services/api";
import { formatNumber } from "../../utils/formatters";
import "./SerpComparison.css";

const SerpComparison = ({ studyId, similarities, keywords = [] }) => {
  const [serpData, setSerpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [availableKeywords, setAvailableKeywords] = useState([]);
  const [hoveredUrl, setHoveredUrl] = useState(null);
  const [urlColorMap, setUrlColorMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const allowedKeywordsSet = React.useMemo(() => {
    return new Set(keywords.map(kw => (kw.keyword || kw).toLowerCase()));
  }, [keywords]);

  const HIGHLIGHT_COLORS = [
    'rgba(168, 85, 247, 0.2)',   // Violet
    'rgba(59, 130, 246, 0.2)',   // Blue
    'rgba(16, 185, 129, 0.2)',   // Green
    'rgba(239, 68, 68, 0.2)',    // Red
    'rgba(245, 158, 11, 0.2)',   // Orange
    'rgba(236, 72, 153, 0.2)',   // Pink
    'rgba(139, 92, 246, 0.2)',   // Purple
    'rgba(6, 182, 212, 0.2)',    // Cyan
    'rgba(132, 204, 22, 0.2)',   // Lime
    'rgba(251, 146, 60, 0.2)',   // Orange light
  ];

  const TRACKING_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'gclsrc', 'dclid', 'fbclid', 'msclkid', 'twclid',
    'mc_cid', 'mc_eid', 'ref', 'affiliate', 'partner',
    'srsltid'
  ];

  const cleanUrl = (url) => {
    if (!url) return '-';
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      
      TRACKING_PARAMS.forEach(param => params.delete(param));
      
      const cleanedSearch = params.toString();
      
      let cleanedUrl = `${urlObj.protocol}//${urlObj.host}${decodeURIComponent(urlObj.pathname)}`;
      
      if (cleanedSearch) {
        cleanedUrl += `?${cleanedSearch}`;
      }
      
      return cleanedUrl;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to parse URL:', url, error);
      }
      return url;
    }
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    
    try {
      const urlObj = new URL(url);
      let baseUrl = urlObj.origin + urlObj.pathname;
      
      baseUrl = baseUrl.replace('://www.', '://');
      
      baseUrl = baseUrl.replace(/\/$/, '');
      
      baseUrl = baseUrl.toLowerCase();
      
      const params = new URLSearchParams(urlObj.search);
      const cleanParams = new URLSearchParams();
      
      for (const [key, value] of params.entries()) {
        if (!TRACKING_PARAMS.includes(key.toLowerCase())) {
          cleanParams.append(key.toLowerCase(), value.toLowerCase());
        }
      }
      
      const queryString = cleanParams.toString();
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    } catch {
      return url.trim();
    }
  };

  useEffect(() => {
    const fetchSerpData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await studiesAPI.getSerpResults(studyId);
        setSerpData(data);
        
        const kwList = data.map(s => s.keyword);
        setAvailableKeywords(kwList);
        
        if (similarities.length > 0) {
          const kw1 = similarities[0].keyword1;
          const kw2 = similarities[0].keyword2;
          setSelectedKeywords([kw1, kw2]);
        } else if (kwList.length >= 2) {
          setSelectedKeywords(kwList.slice(0, 2));
        } else {
          setSelectedKeywords(kwList);
        }
      } catch (err) {
        console.error("Error fetching SERP data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSerpData();
  }, [studyId, similarities]);

  useEffect(() => {
    if (!serpData) return;
    
    if (allowedKeywordsSet.size > 0) {
      const kwList = serpData.map(s => s.keyword).filter(kw => allowedKeywordsSet.has(kw.toLowerCase()));
      setAvailableKeywords(kwList);
      setSelectedKeywords(prev => {
        const filtered = prev.filter(kw => allowedKeywordsSet.has(kw.toLowerCase()));
        if (filtered.length === 0 && kwList.length > 0) {
          return kwList.slice(0, Math.min(2, kwList.length));
        }
        return filtered;
      });
    } else {
      const kwList = serpData.map(s => s.keyword);
      setAvailableKeywords(kwList);
    }
  }, [allowedKeywordsSet, serpData]);

  const toggleKeyword = (keyword) => {
    if (selectedKeywords.includes(keyword)) {
      setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
    } else {
      setSelectedKeywords([...selectedKeywords, keyword]);
    }
    setSearchTerm("");
    setUrlColorMap({});
  };

  const removeKeyword = (keyword) => {
    setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
  };

  const filteredKeywords = availableKeywords.filter(kw => 
    kw.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="serp-comparison-loading">
        <LoadingSpinner size="lg" />
        <p>Chargement des SERPs...</p>
      </div>
    );
  }

  if (error || !serpData) {
    return (
      <div className="serp-comparison-error">
        <Icon name="AlertCircle" size={48} color="var(--color-error)" />
        <p>Erreur lors du chargement des SERPs</p>
        <small>{error || "Données SERP non disponibles"}</small>
      </div>
    );
  }

  if (availableKeywords.length === 0) {
    return (
      <div className="serp-comparison-empty">
        <Icon name="Search" size={48} color="var(--color-gray-400)" />
        <p>Aucune donnée SERP disponible</p>
      </div>
    );
  }

  const getSerpPositions = (keyword) => {
    const serpResult = serpData.find(s => s.keyword === keyword);
    if (!serpResult || !serpResult.positions) return [];
    return serpResult.positions.slice(0, 10);
  };

  const getVolumeForKeyword = (keyword) => {
    if (similarities.length === 0) return 0;
    
    const sim = similarities.find(s => s.keyword1 === keyword || s.keyword2 === keyword);
    if (!sim) return 0;
    
    return sim.keyword1 === keyword ? sim.volume1 : sim.volume2;
  };

  const getUrlCounts = () => {
    const urlCounts = {};
    
    selectedKeywords.forEach(keyword => {
      const positions = getSerpPositions(keyword);
      const urlsInThisSerp = new Set();
      
      positions.forEach(pos => {
        if (!pos.url) return;
        const normalized = normalizeUrl(pos.url);
        if (normalized) {
          urlsInThisSerp.add(normalized);
        }
      });
      
      urlsInThisSerp.forEach(url => {
        if (!urlCounts[url]) {
          urlCounts[url] = 0;
        }
        urlCounts[url]++;
      });
    });
    
    return urlCounts;
  };

  const handleUrlClick = (url) => {
    if (!url) return;

    if (urlColorMap[url] !== undefined) {
      const newMap = { ...urlColorMap };
      delete newMap[url];
      setUrlColorMap(newMap);
    } else {
      const selectedCount = Object.keys(urlColorMap).length;
      if (selectedCount < 10) {
        const colorIndicesInUse = Object.values(urlColorMap);
        const usedColorIndices = new Set(colorIndicesInUse);
        let colorIndexToUse = 0;
        while (usedColorIndices.has(colorIndexToUse) && colorIndexToUse < 10) {
          colorIndexToUse++;
        }
        if (colorIndexToUse < 10) {
          setUrlColorMap({
            ...urlColorMap,
            [url]: colorIndexToUse
          });
        }
      }
    }
  };

  const getUrlColor = (url) => {
    const colorIndex = urlColorMap[url];
    if (colorIndex !== undefined) {
      return HIGHLIGHT_COLORS[colorIndex];
    }
    return null;
  };

  const urlCounts = getUrlCounts();

  return (
    <div className="serp-comparison-container">
      {/* Keyword selector */}
      <div className="keyword-selector-section">
        <label className="selector-label">Sélectionner les mots-clés à comparer :</label>
        
        {/* Dropdown search */}
        <div className="dropdown-container">
          <input
            type="text"
            className="dropdown-search"
            placeholder="Rechercher un mot-clé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsDropdownOpen(true)}
          />
          {isDropdownOpen && (
            <>
              <div 
                className="dropdown-backdrop" 
                onClick={() => setIsDropdownOpen(false)} 
              />
              <div className="dropdown-menu">
                {filteredKeywords.length > 0 ? (
                  filteredKeywords.map(keyword => (
                    <label key={keyword} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={selectedKeywords.includes(keyword)}
                        onChange={() => toggleKeyword(keyword)}
                      />
                      <span>{keyword}</span>
                    </label>
                  ))
                ) : (
                  <div className="dropdown-empty">Aucun résultat</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Selected keywords display */}
        {selectedKeywords.length > 0 && (
          <div className="selected-keywords">
            {selectedKeywords.map(keyword => (
              <span key={keyword} className="keyword-chip selected">
                {keyword}
                <button 
                  onClick={() => removeKeyword(keyword)}
                  className="remove-chip"
                  aria-label="Retirer"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {selectedKeywords.length === 0 && (
          <p className="selector-hint">Sélectionnez au moins un mot-clé pour afficher les SERPs</p>
        )}
      </div>

      {/* SERP Comparison Grid */}
      <div className="serp-scroll-container">

      {selectedKeywords.length > 0 && (
        <div className="serp-comparison-grid">
          {selectedKeywords.map((keyword) => {
            const positions = getSerpPositions(keyword);
            const volume = getVolumeForKeyword(keyword);
            
            return (
              <div key={keyword} className="serp-column">
                <div className="serp-header">
                  <h3>{keyword}</h3>
                  {volume > 0 && (
                    <span className="volume-badge">
                      {formatNumber(volume)} recherches mensuelles
                    </span>
                  )}
                </div>
                <div className="serp-results">
                  {positions.length > 0 ? (
                    positions.map((pos, idx) => {
                      const normalizedUrl = normalizeUrl(pos.url);
                      const isDuplicate = urlCounts[normalizedUrl] >= 2;
                      const hasUrlKey = normalizedUrl !== '';
                      const urlColor = hasUrlKey ? getUrlColor(normalizedUrl) : null;
                      const isHovered = hasUrlKey && hoveredUrl === normalizedUrl;
                      const isClicked = hasUrlKey && urlColorMap[normalizedUrl] !== undefined;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`serp-result-item ${isHovered ? 'highlighted' : ''}`}
                          style={urlColor ? { backgroundColor: urlColor } : {}}
                          onMouseEnter={() => setHoveredUrl(normalizedUrl)}
                          onMouseLeave={() => setHoveredUrl(null)}
                          onClick={() => handleUrlClick(normalizedUrl)}
                        >
                          <div className="serp-rank">{idx + 1}</div>
                          <div className="serp-info">
                            <a 
                              href={pos.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className={`serp-url ${isDuplicate ? 'duplicate' : ''} ${isClicked ? 'clicked' : ''}`}
                              title={pos.url}
                            >
                              {cleanUrl(pos.url)}
                            </a>
                            {pos.title && <div className="serp-title">{pos.title}</div>}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="serp-empty">
                      <Icon name="Search" size={32} color="var(--color-gray-300)" />
                      <p>Aucun résultat</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default SerpComparison;
